import type { V2CreateAdminTypePropertyInput } from '@idc-forms/schemas/api-v2/admin-type-properties'
import {
  CircleOff,
  CirclePlay,
  FileText,
  FolderOpen,
  Images,
  Info,
  Pencil,
  Trash2
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { DocumentPortfolioTab } from '@/features/documents/components/document-portfolio-tab'
import { GalleryTabWithCopy } from '@/features/documents/components/gallery-tab-with-copy'
import { ProjectBadge } from '@/features/projects/components/project-badge'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteTypePropertyV2Mutation,
  useUpdateTypePropertyV2Mutation
} from '../hooks/use-type-property-v2-mutations'
import type { TypePropertyV2AdminRow } from '../models/type-property-admin-v2'
import { TypePropertyActiveBadge } from './type-properties-admin-columns'
import { TypePropertyV2Form } from './type-property-v2-form'

type TypePropertyV2DetailPanelProps = {
  typeProperty: TypePropertyV2AdminRow
}

/**
 * Panel de detalle del subtipo contra la API v2. Espeja el panel v1 —mismo
 * `TypePropertyV2Form`, mismo badge, mismo acordeón de actividad— y sólo cambia el
 * transporte: direcciona por `typ_`, el proyecto llega como `{ id: prj_, name,
 * code }`, y la autoría ya resuelta a `usr_`.
 */
export function TypePropertyV2DetailPanel({ typeProperty }: TypePropertyV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateTypePropertyV2Mutation()
  const deleteMutation = useDeleteTypePropertyV2Mutation()
  const { has } = usePermissions()

  const toggleActive = () => {
    updateMutation.mutate({ id: typeProperty.id, body: { active: !typeProperty.active } })
  }

  if (mode === 'edit') {
    return (
      <TypePropertyV2Form
        defaultValues={{
          // El SDK tipa los ids como string plano; el form usa los branded del
          // schema (`prj_`). Mismo dato en runtime.
          project: typeProperty.project?.id as
            | V2CreateAdminTypePropertyInput['project']
            | undefined,
          code: typeProperty.code,
          name: typeProperty.name ?? undefined,
          active: typeProperty.active,
          // Los atributos del plano se PRECARGAN: sin esto el formulario abre en blanco y
          // guardar sin tocarlos los borra —el campo vacío viaja como `null`, que en el
          // contrato significa «borralo»—. Editar el nombre no puede vaciar los baños.
          bedrooms: typeProperty.bedrooms,
          bathrooms: typeProperty.bathrooms,
          parkingSpaces: typeProperty.parkingSpaces
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: typeProperty.id, body: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">
              {typeProperty.name ?? typeProperty.code}
            </h3>
            {typeProperty.project && (
              <ProjectBadge name={typeProperty.project.name} code={typeProperty.project.code} />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow
              label="Estado"
              value={<TypePropertyActiveBadge active={typeProperty.active} />}
            />
            <DetailFieldRow
              label="Código"
              value={typeProperty.code}
              copyValue={typeProperty.code}
            />
            <DetailFieldRow label="Nombre" value={typeProperty.name ?? '—'} />
            <DetailFieldRow label="Proyecto" value={typeProperty.project?.name ?? '—'} />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateTypeProperties)
              ? [
                  {
                    label: 'Editar subtipo',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    // El editor de plantillas es una PÁGINA completa (`:id/docs`), no un panel.
                    // Navega con el `typ_` porque es el único id que la fila v2 conoce; la API
                    // v1 lo resuelve igual que un `_id` (ver `publicIdFilter`).
                    label: 'Plantillas de documentos',
                    icon: <FileText className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      closePanel()
                      void navigate(PATHS.admin.business.typeProperties.documents(typeProperty.id))
                    }
                  },
                  {
                    label: typeProperty.active ? 'Desactivar subtipo' : 'Activar subtipo',
                    icon: typeProperty.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteTypeProperties)
              ? [
                  {
                    label: 'Eliminar subtipo',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        {/* La lista SCROLLEA en vez de estirarse. Con dos pestañas entraba justo; la
            tercera empujaba el ancho mínimo del contenido del panel y recortaba TODO lo
            de arriba —el encabezado, las acciones— contra el borde izquierdo. Es el mismo
            patrón que ya usa el panel del cliente, que tiene cinco. */}
        <ScrollFade className="w-full" scrollClassName="overflow-y-hidden">
          <TabsList className="flex h-auto w-max min-w-full justify-normal rounded-none">
            <TabsTrigger className="flex gap-1" value="more-info">
              <Info className="size-4" /> Más información
            </TabsTrigger>
            <TabsTrigger className="flex gap-1" value="portfolio">
              <FolderOpen className="size-4" /> Cartapacio
            </TabsTrigger>
            {/* La galería va aparte del cartapacio porque el trabajo es otro: ahí se
              clasifica una lista de papeles, acá se decide qué se ve y en qué orden. */}
            <TabsTrigger className="flex gap-1" value="gallery">
              <Images className="size-4" /> Galería
            </TabsTrigger>
          </TabsList>
        </ScrollFade>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['activity']}>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Creado"
                    value={formatHumanDateTime(typeProperty.createdAt)}
                  />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={typeProperty.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(typeProperty.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={typeProperty.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
        <TabsContent value="portfolio">
          <DocumentPortfolioTab ownerType="type-property" ownerId={typeProperty.id} />
        </TabsContent>
        <TabsContent value="gallery">
          <GalleryTabWithCopy
            ownerType="type-property"
            ownerId={typeProperty.id}
            // El CÓDIGO y no el nombre: tres de los cuatro proyectos tienen ocho o diez
            // tipologías llamadas todas «APARTAMENTO», así que el nombre no identifica
            // nada y el asesor no sabría a cuál le está subiendo las fotos.
            ownerLabel={typeProperty.code}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar subtipo</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{typeProperty.name ?? typeProperty.code}”. Los inmuebles que lo
              referencian se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(typeProperty.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
